import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map, switchMap, catchError, tap } from 'rxjs/operators';
import { SegurosApiService } from './seguros-api.service';
import {
  KPIsResponse,
  StatusDistribution,
  StatusDistributionResponse,
  CreationTrend,
  CreationTrendResponse,
  RecentActivity,
  PoliciesResponse,
  ErrorPoliciesResponse,
  AnalyticsAggregation,
  DateFilter,
  AggregationType,
  DashboardQueryParams,
  InsurerWidgetSummary
} from '../models/seguros.interface';

export interface DashboardData {
  kpis: KPIsResponse | null;
  statusDistribution: StatusDistributionResponse | null;
  creationTrend: CreationTrendResponse | null;
  recentActivity: RecentActivity[] | null;
  loading: boolean;
  error: string | null;
}

export interface AnalyticsData {
  geography: any[] | null;
  age: any[] | null;
  profession: any[] | null;
  product: any[] | null;
  vehicleBrand: any[] | null;
  vehicleAge: any[] | null;
  loading: boolean;
  error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class SegurosDataService {
  
  // Date filter state
  private dateFilterSubject = new BehaviorSubject<DateFilter>({});
  public dateFilter$ = this.dateFilterSubject.asObservable();

  // Insurer filter state
  private insurerFilterSubject = new BehaviorSubject<string | null>(null);
  public insurerFilter$ = this.insurerFilterSubject.asObservable();

  // Dashboard data state
  private dashboardDataSubject = new BehaviorSubject<DashboardData>({
    kpis: null,
    statusDistribution: null,
    creationTrend: null,
    recentActivity: null,
    loading: false,
    error: null
  });
  public dashboardData$ = this.dashboardDataSubject.asObservable();

  // Analytics data state
  private analyticsDataSubject = new BehaviorSubject<AnalyticsData>({
    geography: null,
    age: null,
    profession: null,
    product: null,
    vehicleBrand: null,
    vehicleAge: null,
    loading: false,
    error: null
  });
  public analyticsData$ = this.analyticsDataSubject.asObservable();

  // Policies data state
  private policiesDataSubject = new BehaviorSubject<PoliciesResponse | null>(null);
  public policiesData$ = this.policiesDataSubject.asObservable();

  // Error policies data state
  private errorPoliciesDataSubject = new BehaviorSubject<ErrorPoliciesResponse | null>(null);
  public errorPoliciesData$ = this.errorPoliciesDataSubject.asObservable();

  // Payment error policies data state
  private paymentErrorPoliciesDataSubject = new BehaviorSubject<ErrorPoliciesResponse | null>(null);
  public paymentErrorPoliciesData$ = this.paymentErrorPoliciesDataSubject.asObservable();

  // Verification error policies data state
  private verificationErrorPoliciesDataSubject = new BehaviorSubject<ErrorPoliciesResponse | null>(null);
  public verificationErrorPoliciesData$ = this.verificationErrorPoliciesDataSubject.asObservable();

  // Vehicle use error policies data state
  private vehicleUseErrorPoliciesDataSubject = new BehaviorSubject<ErrorPoliciesResponse | null>(null);
  public vehicleUseErrorPoliciesData$ = this.vehicleUseErrorPoliciesDataSubject.asObservable();

  // Default address policies data state
  private defaultAddressPoliciesDataSubject = new BehaviorSubject<ErrorPoliciesResponse | null>(null);
  public defaultAddressPoliciesData$ = this.defaultAddressPoliciesDataSubject.asObservable();

  // Widget summaries por aseguradora — Map<insurerCode, InsurerWidgetSummary>.
  // Cada entrada representa el resumen ligero (KPIs + status distribution) que
  // se muestra en cada widget del grid del dashboard.
  private widgetSummariesSubject = new BehaviorSubject<Map<string, InsurerWidgetSummary>>(new Map());
  public widgetSummaries$ = this.widgetSummariesSubject.asObservable();

  // Count de pólizas con error de notificación de pago por aseguradora.
  // Map<insurerCode, totalItems>. Se alimenta con N llamadas paralelas a
  // /policies/payment-errors?insurer_code=X&limit=1 — solo el totalItems de
  // pagination es lo que usa el tab Analítica Avanzada para el split de
  // Pagadas (saneadas vs con error). El listado completo de la data sigue
  // viviendo en paymentErrorPoliciesDataSubject (lo usa el tab payment_errors).
  private paymentErrorsCountByInsurerSubject = new BehaviorSubject<Map<string, number>>(new Map());
  public paymentErrorsCountByInsurer$ = this.paymentErrorsCountByInsurerSubject.asObservable();

  // Loading states
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(private segurosApi: SegurosApiService) {
    // El auto-load del dashboard al cambiar el filtro de fechas se removió:
    // el componente (insurance-console) ahora gestiona explícitamente las
    // recargas del panel detalle scoped a la aseguradora seleccionada y de
    // los widget summaries. Mantener el auto-trigger generaría doble fetch
    // (uno sin scope desde acá + uno con scope desde el componente).
  }

  // Date filter methods
  setDateFilter(dateFilter: DateFilter): void {
    this.dateFilterSubject.next(dateFilter);
  }

  getCurrentDateFilter(): DateFilter {
    return this.dateFilterSubject.value;
  }

  // Insurer filter methods
  setInsurerFilter(insurerCode: string | null): void {
    this.insurerFilterSubject.next(insurerCode);
    // Reload all data when insurer filter changes
    this.reloadAllDataWithFilters();
  }

  getCurrentInsurerFilter(): string | null {
    return this.insurerFilterSubject.value;
  }

  clearInsurerFilter(): void {
    this.insurerFilterSubject.next(null);
    this.reloadAllDataWithFilters();
  }

  private reloadAllDataWithFilters(): void {
    this.loadDashboardData();
    this.loadAnalyticsData();
  }

  // Dashboard data loading
  // El argumento `insurerCode` se usa para el panel detalle del dashboard rediseñado:
  // siempre carga datos scoped a UNA aseguradora específica, independientemente del
  // filtro global. Si no se pasa, se usa el filtro global (compatibilidad).
  loadDashboardData(dateFilter?: DateFilter, insurerCode?: string | null): void {
    const filter = dateFilter || this.getCurrentDateFilter();
    const effectiveInsurerCode = insurerCode !== undefined ? insurerCode : this.getCurrentInsurerFilter();

    // Merge filters
    const params: DashboardQueryParams = {
      ...filter,
      ...(effectiveInsurerCode && { insurer_code: effectiveInsurerCode })
    };
    
    this.dashboardDataSubject.next({
      ...this.dashboardDataSubject.value,
      loading: true,
      error: null
    });

    // Load all dashboard data in parallel
    combineLatest([
      this.segurosApi.getDashboardKPIs(params).pipe(
        catchError(error => {
          console.error('❌ Error loading KPIs:', error);
          return of(null);
        })
      ),
      this.segurosApi.getStatusDistribution(params).pipe(
        catchError(error => {
          console.error('❌ Error loading status distribution:', error);
          return of(null);
        })
      ),
      this.segurosApi.getCreationTrend(params).pipe(
        catchError(error => {
          console.error('❌ Error loading creation trend:', error);
          return of(null);
        })
      ),
      this.segurosApi.getRecentActivity({ limit: 10, ...(insurerCode && { insurer_code: insurerCode }) }).pipe(
        catchError(error => {
          console.error('❌ Error loading recent activity:', error);
          return of(null);
        })
      )
    ]).pipe(
      catchError(error => {
        console.error('❌ Error in combineLatest:', error);
        this.dashboardDataSubject.next({
          ...this.dashboardDataSubject.value,
          loading: false,
          error: 'Error al cargar los datos del dashboard'
        });
        return of([null, null, null, null]);
      })
    ).subscribe(([kpis, statusDistribution, creationTrend, recentActivity]) => {
      console.log('📈 Dashboard data received:');
      console.log('- KPIs:', kpis);
      console.log('- Status Distribution:', statusDistribution);
      console.log('- Creation Trend:', creationTrend);
      console.log('- Recent Activity:', recentActivity);
      
      this.dashboardDataSubject.next({
        kpis: kpis as KPIsResponse,
        statusDistribution: statusDistribution as StatusDistributionResponse,
        creationTrend: creationTrend as CreationTrendResponse,
        recentActivity: recentActivity as RecentActivity[],
        loading: false,
        error: null
      });
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Widget summaries (grid del dashboard rediseñado)
  // ─────────────────────────────────────────────────────────────────────────

  // Carga el resumen ligero de UNA aseguradora (KPIs + status distribution).
  // Se usa para alimentar el widget del grid en el tab dashboard.
  loadWidgetSummary(insurerCode: string, dateFilter?: DateFilter): void {
    const filter = dateFilter || this.getCurrentDateFilter();
    const params: DashboardQueryParams = {
      ...filter,
      insurer_code: insurerCode
    };

    // Marcar loading antes de disparar las llamadas
    this.updateWidgetSummary(insurerCode, { loading: true, error: null });

    combineLatest([
      this.segurosApi.getDashboardKPIs(params).pipe(catchError(() => of(null))),
      this.segurosApi.getStatusDistribution(params).pipe(catchError(() => of(null)))
    ]).subscribe({
      next: ([kpis, statusDistribution]) => {
        const hadError = !kpis && !statusDistribution;
        this.updateWidgetSummary(insurerCode, {
          kpis: kpis as KPIsResponse | null,
          statusDistribution: statusDistribution as StatusDistributionResponse | null,
          loading: false,
          error: hadError ? 'No se pudo cargar el resumen' : null
        });
      },
      error: () => {
        this.updateWidgetSummary(insurerCode, {
          loading: false,
          error: 'Error al cargar el resumen'
        });
      }
    });
  }

  // Carga el resumen de varias aseguradoras en paralelo (uno por código).
  loadAllWidgetSummaries(insurerCodes: string[], dateFilter?: DateFilter): void {
    insurerCodes.forEach(code => this.loadWidgetSummary(code, dateFilter));
  }

  // Invalida todos los resúmenes (útil al cambiar filtros globales).
  invalidateAllWidgetSummaries(): void {
    this.widgetSummariesSubject.next(new Map());
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Payment notif errors count per aseguradora (tab Analítica Avanzada)
  // ─────────────────────────────────────────────────────────────────────────

  // Carga en paralelo el count de pólizas con error de notif de pago para
  // cada aseguradora. Usa limit=1 — solo necesitamos pagination.totalItems.
  // Si una llamada falla, esa aseguradora queda con count 0 (no rompe el
  // resto). Al terminar, emite el Map completo.
  loadPaymentErrorsCountByInsurer(insurerCodes: string[], dateFilter?: DateFilter): void {
    const filter = dateFilter || this.getCurrentDateFilter();

    if (!insurerCodes || insurerCodes.length === 0) {
      this.paymentErrorsCountByInsurerSubject.next(new Map());
      return;
    }

    const requests = insurerCodes.map(code => {
      const params: any = {
        page: 1,
        limit: 1,
        insurer_code: code
      };
      if (filter?.date_from) params.date_from = filter.date_from;
      if (filter?.date_to) params.date_to = filter.date_to;
      return this.segurosApi.getPaymentErrorPolicies(params).pipe(
        catchError(() => of({ pagination: { totalItems: 0 } } as any))
      );
    });

    combineLatest(requests).subscribe(responses => {
      const map = new Map<string, number>();
      insurerCodes.forEach((code, i) => {
        const totalItems = (responses[i] as any)?.pagination?.totalItems || 0;
        map.set(code, totalItems);
      });
      this.paymentErrorsCountByInsurerSubject.next(map);
    });
  }

  // Helper privado para mergear un parche en la entrada del Map sin perder
  // los campos anteriores (típico patrón de actualización inmutable).
  private updateWidgetSummary(insurerCode: string, patch: Partial<InsurerWidgetSummary>): void {
    const current = this.widgetSummariesSubject.value;
    const existing: InsurerWidgetSummary = current.get(insurerCode) || {
      insurerCode,
      kpis: null,
      statusDistribution: null,
      loading: false,
      error: null
    };
    const updated = new Map(current);
    updated.set(insurerCode, { ...existing, ...patch, insurerCode });
    this.widgetSummariesSubject.next(updated);
  }

  // Analytics data loading
  // `extraFilters` permite pasar policy_status/notification_status desde los
  // chips del análisis demográfico (Plan C) sin re-derivar el dateFilter aquí.
  loadAnalyticsData(dateFilter?: DateFilter, extraFilters?: {
    policy_status?: string;
    notification_status?: 'notified_ok' | 'notification_error';
    frequency?: string;
  }): void {
    const filter = dateFilter || this.getCurrentDateFilter();
    const insurerCode = this.getCurrentInsurerFilter();

    // Merge filters
    const baseParams: any = {
      ...filter,
      ...(insurerCode && { insurer_code: insurerCode }),
      ...(extraFilters?.policy_status && { policy_status: extraFilters.policy_status }),
      ...(extraFilters?.notification_status && { notification_status: extraFilters.notification_status }),
      ...(extraFilters?.frequency && { frequency: extraFilters.frequency })
    };
    
    this.analyticsDataSubject.next({
      ...this.analyticsDataSubject.value,
      loading: true,
      error: null
    });

    // Load all analytics data in parallel
    combineLatest([
      this.segurosApi.getGeographyAnalytics(baseParams),
      this.segurosApi.getAgeAnalytics(baseParams),
      this.segurosApi.getProfessionAnalytics(baseParams),
      this.segurosApi.getProductAnalytics(baseParams),
      this.segurosApi.getVehicleBrandAnalytics(baseParams),
      this.segurosApi.getVehicleAgeAnalytics(baseParams)
    ]).pipe(
      catchError(error => {
        console.error('Error loading analytics data:', error);
        this.analyticsDataSubject.next({
          ...this.analyticsDataSubject.value,
          loading: false,
          error: 'Error al cargar los datos de analytics'
        });
        return of([null, null, null, null, null, null]);
      })
    ).subscribe(([geography, age, profession, product, vehicleBrand, vehicleAge]) => {
      this.analyticsDataSubject.next({
        geography: geography as any[],
        age: age as any[],
        profession: profession as any[],
        product: product as any[],
        vehicleBrand: vehicleBrand as any[],
        vehicleAge: vehicleAge as any[],
        loading: false,
        error: null
      });
    });
  }

  // Policies data loading
  loadPoliciesData(params?: any): void {
    this.loadingSubject.next(true);
    const currentFilter = this.dateFilterSubject.getValue();
    const insurerCode = this.getCurrentInsurerFilter();
    const mergedParams = {
      ...params,
      date_from: currentFilter.date_from,
      date_to: currentFilter.date_to,
      ...(insurerCode && { insurer_code: insurerCode })
    };
    console.log('📅 Loading policies with date filter:', mergedParams);
    
    this.segurosApi.getPolicies(mergedParams).pipe(
      catchError(error => {
        console.error('Error loading policies:', error);
        return of(null);
      }),
      tap(() => this.loadingSubject.next(false))
    ).subscribe(data => {
      this.policiesDataSubject.next(data);
    });
  }

  // Search policies data loading
  searchPoliciesData(params?: any): void {
    this.loadingSubject.next(true);
    const currentFilter = this.dateFilterSubject.getValue();
    const insurerCode = this.getCurrentInsurerFilter();
    const mergedParams = {
      ...params,
      date_from: currentFilter.date_from,
      date_to: currentFilter.date_to,
      ...(insurerCode && { insurer_code: insurerCode })
    };
    console.log('🔍 Searching policies with parameters:', mergedParams);
    
    this.segurosApi.searchPolicies(mergedParams).pipe(
      catchError(error => {
        console.error('Error searching policies:', error);
        return of(null);
      }),
      tap(() => this.loadingSubject.next(false))
    ).subscribe(data => {
      this.policiesDataSubject.next(data);
    });
  }

  // Error policies data loading
  loadErrorPoliciesData(params?: any): void {
    this.loadingSubject.next(true);
    const currentFilter = this.dateFilterSubject.getValue();
    const insurerCode = this.getCurrentInsurerFilter();
    
    // Aplicar filtros de fecha y aseguradora
    const mergedParams = {
      ...params,
      date_from: currentFilter.date_from,
      date_to: currentFilter.date_to,
      ...(insurerCode && { insurer_code: insurerCode })
    };
    console.log('📅 Loading error policies with merged params:', mergedParams);
    console.log('🔍 Original currentFilter:', currentFilter);
    console.log('🔍 Params being sent to API:', params);
    
    this.segurosApi.getErrorPolicies(mergedParams).pipe(
      catchError(error => {
        console.error('Error loading error policies:', error);
        return of(null);
      }),
      tap(() => this.loadingSubject.next(false))
    ).subscribe(data => {
      console.log('📊 Error policies loaded:', {
        totalItems: data?.pagination?.totalItems,
        currentPage: data?.pagination?.currentPage,
        totalPages: data?.pagination?.totalPages,
        dataLength: data?.data?.length
      });
      this.errorPoliciesDataSubject.next(data);
    });
  }

  // Payment error policies data loading
  loadPaymentErrorPoliciesData(params?: any): void {
    this.loadingSubject.next(true);
    const currentFilter = this.dateFilterSubject.getValue();
    const insurerCode = this.getCurrentInsurerFilter();
    
    // Aplicar filtros de fecha y aseguradora
    const mergedParams = {
      ...params,
      date_from: currentFilter.date_from,
      date_to: currentFilter.date_to,
      ...(insurerCode && { insurer_code: insurerCode })
    };
    console.log('📅 Loading payment error policies with merged params:', mergedParams);
    console.log('🔍 Original currentFilter:', currentFilter);
    console.log('🔍 Params being sent to API:', params);
    
    this.segurosApi.getPaymentErrorPolicies(mergedParams).pipe(
      catchError(error => {
        console.error('Error loading payment error policies:', error);
        return of(null);
      }),
      tap(() => this.loadingSubject.next(false))
    ).subscribe(data => {
      console.log('📊 Payment error policies loaded:', {
        totalItems: data?.pagination?.totalItems,
        currentPage: data?.pagination?.currentPage,
        totalPages: data?.pagination?.totalPages,
        dataLength: data?.data?.length
      });
      this.paymentErrorPoliciesDataSubject.next(data);
    });
  }

  // Verification error policies data loading
  loadVerificationErrorPoliciesData(params?: any): void {
    this.loadingSubject.next(true);
    const currentFilter = this.dateFilterSubject.getValue();
    const insurerCode = this.getCurrentInsurerFilter();
    
    const mergedParams = {
      ...params,
      date_from: currentFilter.date_from,
      date_to: currentFilter.date_to,
      ...(insurerCode && { insurer_code: insurerCode })
    };
    console.log('📅 Loading verification error policies with merged params:', mergedParams);
    
    this.segurosApi.getVerificationErrorPolicies(mergedParams).pipe(
      catchError(error => {
        console.error('Error loading verification error policies:', error);
        return of(null);
      }),
      tap(() => this.loadingSubject.next(false))
    ).subscribe(data => {
      console.log('📊 Verification error policies loaded:', {
        totalItems: data?.pagination?.totalItems,
        currentPage: data?.pagination?.currentPage,
        totalPages: data?.pagination?.totalPages,
        dataLength: data?.data?.length
      });
      this.verificationErrorPoliciesDataSubject.next(data);
    });
  }

  // Vehicle use error policies data loading
  loadVehicleUseErrorPoliciesData(params?: any): void {
    this.loadingSubject.next(true);
    const currentFilter = this.dateFilterSubject.getValue();
    const insurerCode = this.getCurrentInsurerFilter();
    
    const mergedParams = {
      ...params,
      date_from: currentFilter.date_from,
      date_to: currentFilter.date_to,
      ...(insurerCode && { insurer_code: insurerCode })
    };
    console.log('📅 Loading vehicle use error policies with merged params:', mergedParams);
    
    this.segurosApi.getVehicleUseErrorPolicies(mergedParams).pipe(
      catchError(error => {
        console.error('Error loading vehicle use error policies:', error);
        return of(null);
      }),
      tap(() => this.loadingSubject.next(false))
    ).subscribe(data => {
      console.log('📊 Vehicle use error policies loaded:', {
        totalItems: data?.pagination?.totalItems,
        currentPage: data?.pagination?.currentPage,
        totalPages: data?.pagination?.totalPages,
        dataLength: data?.data?.length
      });
      this.vehicleUseErrorPoliciesDataSubject.next(data);
    });
  }

  // Default address policies data loading
  loadDefaultAddressPoliciesData(params?: any): void {
    this.loadingSubject.next(true);
    const currentFilter = this.dateFilterSubject.getValue();
    const insurerCode = this.getCurrentInsurerFilter();
    
    const mergedParams = {
      ...params,
      date_from: currentFilter.date_from,
      date_to: currentFilter.date_to,
      ...(insurerCode && { insurer_code: insurerCode })
    };
    console.log('📅 Loading default address policies with merged params:', mergedParams);
    
    this.segurosApi.getDefaultAddressPolicies(mergedParams).pipe(
      catchError(error => {
        console.error('Error loading default address policies:', error);
        return of(null);
      }),
      tap(() => this.loadingSubject.next(false))
    ).subscribe(data => {
      console.log('📊 Default address policies loaded:', {
        totalItems: data?.pagination?.totalItems,
        currentPage: data?.pagination?.currentPage,
        totalPages: data?.pagination?.totalPages,
        dataLength: data?.data?.length
      });
      this.defaultAddressPoliciesDataSubject.next(data);
    });
  }

  // Refresh policy
  refreshPolicy(policyCode: string): Observable<boolean> {
    return this.segurosApi.refreshPolicyView({ policy_code: policyCode }).pipe(
      map(response => response.success),
      catchError(error => {
        console.error('Error refreshing policy:', error);
        return of(false);
      })
    );
  }

  // Utility methods
  refreshAllData(): void {
    this.loadDashboardData();
    this.loadAnalyticsData();
  }

  // Quick date filter presets
  setTodayFilter(): void {
    const today = new Date().toISOString().split('T')[0];
    this.setDateFilter({ date_from: today, date_to: today });
  }

  setLast7DaysFilter(): void {
    const today = new Date();
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    this.setDateFilter({ 
      date_from: sevenDaysAgo.toISOString().split('T')[0],
      date_to: today.toISOString().split('T')[0]
    });
  }

  setLast30DaysFilter(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    this.setDateFilter({ 
      date_from: thirtyDaysAgo.toISOString().split('T')[0],
      date_to: today.toISOString().split('T')[0]
    });
  }

  // Policy actions
  // El param `insuranceCode` decide a qué endpoint delegar:
  //   'SM' → resume_subscribe_sm   (heredado, sin cambios)
  //   'SC' → retry_emission_sc     (Plan 7 SC/RCV; el endpoint HTTP lo expone Plan 8)
  // Default sigue siendo SM para no romper llamadas viejas que aún pasen solo el code.
  retryPolicy(policyCode: string, insuranceCode?: string): Observable<any> {
    if (insuranceCode === 'SC') {
      return this.segurosApi.retryEmissionSC(policyCode);
    }
    return this.segurosApi.resumeSubscribeSM(policyCode);
  }

  getPolicyDetail(policyId: string): Observable<any> {
    return this.segurosApi.getPolicyDetail(policyId);
  }

  // Error resolution actions
  resolveEmissionError(policyId: string, resolutionComment: string): Observable<any> {
    return this.segurosApi.resolveEmissionError(policyId, resolutionComment);
  }

  resolvePaymentError(policyId: string, resolutionComment: string): Observable<any> {
    return this.segurosApi.resolvePaymentError(policyId, resolutionComment);
  }

  retryPaymentNotification(policyCode: string): Observable<any> {
    return this.segurosApi.retryPaymentNotification(policyCode);
  }

  setCurrentYearFilter(): void {
    const currentYear = new Date().getFullYear();
    this.setDateFilter({ 
      date_from: `${currentYear}-01-01`,
      date_to: `${currentYear}-12-31`
    });
  }

  clearDateFilter(): void {
    this.setDateFilter({});
  }

  // Get insurers
  getInsurers(): Observable<any[]> {
    return this.segurosApi.getInsurers();
  }

  // Get latest sync log
  getLatestSyncLog(params?: { page?: number; limit?: number }): Observable<any> {
    return this.segurosApi.getLatestSyncLog(params);
  }
}
